import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Collapse, Progress, Spin, Tag, Modal, Button } from 'antd';
import {
  CaretRightOutlined,
  CheckCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import './style.scss';
import { getProgressByUserId } from '../../../../../Constants/Api/functions/function';

const UserProgress = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const language = localStorage.getItem('language');
  const [items, setItems] = useState([]);
  const [selectedQuizAttempt, setSelectedQuizAttempt] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  useEffect(() => {
    const handleGetUserProgress = async () => {
      setIsLoading(true);
      try {
        const response = await getProgressByUserId(id);
        setItems(getItems(response));
      } catch (error) {
        console.error('handleGetUserProgress', error);
      } finally {
        setIsLoading(false);
      }
    };

    handleGetUserProgress();
  }, [id]);

  const calculatePercentage = (content) => {
    if (!content || content.length === 0) return 0;
    const total = content.length;
    const completed = content.filter((item) => item?.participation?.id).length;
    return parseFloat(((completed / total) * 100).toFixed(2));
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'success';
      case 'B':
        return 'processing';
      case 'C':
        return 'warning';
      case 'S':
        return 'geekblue';
      default:
        return 'error';
    }
  };

  const handleOpenAttemptHistory = (quizData, contentTitle) => {
    setSelectedQuizAttempt({
      ...quizData,
      contentTitle
    });
    setIsHistoryModalOpen(true);
  };

  const renderLabel = (item) => (
    <div className="progress-course__label">
      <div className="title-and-grade">
        <span className="title">{item?.name}</span>
        {item?.totalQuizzes > 0 && (
          <div className="grade-pill-wrap">
            {item?.calculatedGrade !== 'N/A' ? (
              <Tag
                color={getGradeColor(item.calculatedGrade)}
                style={{ fontWeight: 700, fontSize: '0.85rem', padding: '2px 10px', borderRadius: 6 }}
              >
                🏆 Grade: {item.calculatedGrade} ({item.overallQuizPercentage}%)
              </Tag>
            ) : (
              <Tag color="default" style={{ fontSize: '0.8rem', borderRadius: 6 }}>
                Quizzes Incomplete
              </Tag>
            )}
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              ({item.completedQuizzes}/{item.totalQuizzes} Quizzes Completed • {item.totalEarnedQuizMarks}/{item.totalCourseQuizMarks} Marks)
            </span>
          </div>
        )}
      </div>

      <div className="progress">
        <Progress
          percent={calculatePercentage(item.content)}
          status="active"
          strokeColor={{ from: '#673bf9', to: '#B100C6' }}
        />
      </div>
    </div>
  );

  const renderBody = useMemo(
    () => (content) => (
      <div className="progress-course__body">
        <ul>
          {content.map((item, index) => (
            <li key={index.toString()}>
              <div className="content">
                <div className="lesson-left">
                  <p className="lesson-title">{item?.title}</p>
                </div>

                <div className="lesson-right">
                  {item?.quiz?.hasQuiz && (
                    <div className="quiz-status-pill">
                      {item.quiz.attempt ? (
                        <Button
                          size="small"
                          type="dashed"
                          style={{
                            borderColor: '#86efac',
                            background: '#f0fdf4',
                            color: '#15803d',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAttemptHistory(item.quiz, item.title);
                          }}
                        >
                          <CheckCircleOutlined style={{ color: '#22c55e' }} />
                          Quiz: {item.quiz.attempt.score}/{item.quiz.totalMarks} ({item.quiz.attempt.percentage}%) - {item.quiz.attempt.grade}
                        </Button>
                      ) : (
                        <Tag color="default" style={{ color: '#64748b', borderRadius: 4 }}>
                          Quiz: Pending ({item.quiz.totalQuestions} Qs • {item.quiz.totalMarks} Marks)
                        </Tag>
                      )}
                    </div>
                  )}

                  <div className="is-completed">
                    {item?.participation?.id ? (
                      <span className="completed" title="Lesson Completed">
                        <CheckRoundedIcon />
                      </span>
                    ) : (
                      <span className="not" title="Lesson Not Completed">
                        <CheckRoundedIcon />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    ),
    [language]
  );

  const getItems = useMemo(
    () => (progressData) => {
      if (progressData) {
        return progressData.map((item, index) => ({
          key: index.toString(),
          label: renderLabel(item),
          children: renderBody(item.content),
          style: { background: '#ffffff', borderRadius: 12, marginBottom: 16 },
        }));
      }
      return [];
    },
    [renderLabel, renderBody]
  );

  return (
    <div className="user-progress">
      <Spin spinning={isLoading} size="large" tip="Loading...">
        <div className="progress-courses">
          <Collapse
            bordered={false}
            defaultActiveKey={['0']}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            items={items}
          />
        </div>
      </Spin>

      {/* Quiz Attempt Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined style={{ color: '#4f46e5' }} />
            <span>Quiz Attempt Details: {selectedQuizAttempt?.contentTitle}</span>
          </div>
        }
        open={isHistoryModalOpen}
        onCancel={() => setIsHistoryModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsHistoryModalOpen(false)}>
            Close
          </Button>
        ]}
        width={500}
      >
        {selectedQuizAttempt?.attempt ? (
          <div style={{ padding: '10px 0' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                background: '#f8fafc',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16
              }}
            >
              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Score</span>
                <p style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: '#1e293b' }}>
                  {selectedQuizAttempt.attempt.score} / {selectedQuizAttempt.totalMarks}
                </p>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Percentage</span>
                <p style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: '#4f46e5' }}>
                  {selectedQuizAttempt.attempt.percentage}%
                </p>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Grade</span>
                <p style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: '#16a34a' }}>
                  {selectedQuizAttempt.attempt.grade}
                </p>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Submitted At</span>
                <p style={{ fontSize: '0.9rem', margin: 0, color: '#475569' }}>
                  {new Date(selectedQuizAttempt.attempt.submittedAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
              ✓ Single attempt recorded. Marks and grade contribute to overall course grade.
            </p>
          </div>
        ) : (
          <p>No attempt details available.</p>
        )}
      </Modal>
    </div>
  );
};

export default UserProgress;